import { useEffect, useRef, useState } from 'react';

import { useDashboardAccountScope } from './voterAccountScope';
import { isAbortError, toVoterRequestErrorMessage, type VoterFetchImpl } from './voterApi';

export type VoterRequestState<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
};

export const useVoterRequest = <T,>(
  requestKey: string,
  load: (signal: AbortSignal) => Promise<T>,
  fallbackMessage: string,
  fetchImpl?: VoterFetchImpl,
  enabled = true,
): VoterRequestState<T> => {
  const accountScope = useDashboardAccountScope();
  const loadRef = useRef(load);
  loadRef.current = load;
  const [refetchCount, setRefetchCount] = useState(0);
  const fullKey = enabled ? `${accountScope}\u0000${requestKey}\u0000${refetchCount}` : `${accountScope}\u0000disabled`;
  const [appliedKey, setAppliedKey] = useState(fullKey);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  if (appliedKey !== fullKey) {
    setAppliedKey(fullKey);
    setData(null);
    setLoading(enabled);
    setError(null);
  }

  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError(null);

    void loadRef
      .current(controller.signal)
      .then(next => {
        if (!active || controller.signal.aborted) return;
        setData(next);
        setError(null);
        setLoading(false);
      })
      .catch((requestError: unknown) => {
        if (!active || controller.signal.aborted || isAbortError(requestError)) return;
        setData(null);
        setError(toVoterRequestErrorMessage(requestError, fallbackMessage));
        setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [enabled, fallbackMessage, fetchImpl, fullKey]);

  const current = enabled && appliedKey === fullKey;
  return {
    data: current ? data : null,
    loading: !enabled || appliedKey !== fullKey || loading,
    error: current ? error : null,
    refetch: () => setRefetchCount(count => count + 1),
  };
};
